import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { PiUserListLight } from 'react-icons/pi';
import GlassCard from '../shared/brand/GlassCard';
import Button from '../shared/TailwindComponents/Button';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { fetchMatterDropdowns } from '../../store/actions/Matters/MatterActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import Badge from '../shared/TailwindComponents/Badge';

export interface MatterClient {
  client_id: number;
  client_uuid: string;
  name: string;
  email: string;
  role_name: string;
  role_id: string;
  contact_phone?: string;
  added_at?: string;
}

interface MatterClientsProps {
  matterUuid: string;
  clients?: MatterClient[];
  canEdit?: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

/**
 * useAddPartyPanel - the ONE way to open the add/manage-parties flow.
 *
 * The opener used to live inside this component, so anything else that wanted
 * an "Add party" affordance had no way to reach it. The Context pane's Add
 * button therefore called `goToTab('Details')` instead - which switches tab
 * and reads, to anyone who clicks it, as the screen refreshing and nothing
 * happening.
 *
 * The hook owns the role-options fetch too, since the panel cannot be opened
 * usefully without them.
 */
export function useAddPartyPanel(
  matterUuid: string,
  existingClients: MatterClient[],
  onUpdate?: () => void
) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [clientRoles, setClientRoles] = useState<DropdownOption[]>([]);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchMatterDropdowns())
      .then((result: any) => {
        if (!cancelled && result?.clientRoles) setClientRoles(result.clientRoles);
      })
      .catch((error: any) => console.error('Failed to load client roles:', error));
    return () => { cancelled = true; };
  }, [dispatch]);

  const open = useCallback(() => {
    if (!matterUuid) return;
    setOpening(true);
    try {
      dispatch(showSidePanel({
        type: 'addClientToMatter',
        showSidePanel: true,
        size: 'md',
        message: t('Manage Contacts'),
        icon: <PiUserListLight className="w-5 h-5" />,
        subMsg: t('Add or remove contacts linked to this matter.'),
        matterUuid,
        clientRoleOptions: clientRoles,
        existingMatterClients: existingClients,
        onCancel: () => dispatch(hideSidePanel()),
        onRefresh: async () => { onUpdate?.(); },
        onAfterSave: async () => { onUpdate?.(); },
      } as any));
    } catch (error) {
      console.error('Error opening manage contacts panel:', error);
    } finally {
      setOpening(false);
    }
  }, [dispatch, matterUuid, clientRoles, existingClients, t, onUpdate]);

  return { open, opening };
}

const MatterClients: React.FC<MatterClientsProps> = ({
  matterUuid,
  clients = [],
  canEdit = false,
  onUpdate,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [localClients, setLocalClients] = useState<MatterClient[]>(clients);

  useEffect(() => {
    setLocalClients(clients);
  }, [clients]);

  const { open: openAddParty, opening } = useAddPartyPanel(matterUuid, localClients, onUpdate);

  const handleNavigateToClient = (clientUuid: string) => {
    history.push(`/contacts/${clientUuid}`);
  };

  if (isLoading) {
    return (
      <GlassCard icon={<PiUserListLight />} title={t('Clients')}>
        <Skeleton variant="option" rows={2} />
      </GlassCard>
    );
  }

  return (
    <GlassCard
      icon={<PiUserListLight />}
      title={`${t('Clients')} (${localClients.length})`}
      actions={canEdit && (
        <Button
          variant="outline"
          size="small"
          onClick={openAddParty}
          disabled={opening}
        >
          {opening ? t('Loading...') : t('Manage')}
        </Button>
      )}
    >

      {localClients.length > 0 ? (
        <div className="space-y-2">
          {localClients.map((client, index) => (
            <div
              key={`${client.client_uuid || client.client_id || 'row'}-${index}`}
              className="flex items-center justify-between gap-3 p-3 bg-surface/80 rounded-lg border border-hairline-soft hover:shadow-md transition-shadow"
            >
              {/* Client Info */}
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => handleNavigateToClient(client.client_uuid)}
                  className="block w-full text-sm font-medium text-gray-900 dark:text-gray-100 hover:underline hover:decoration-black dark:hover:decoration-white transition-all cursor-pointer text-start truncate"
                  title={client.name}
                >
                  {client.name}
                </button>
                {client.email && (
                  <div className="flex items-center gap-2 mt-1 min-w-0">
                    <a
                      href={`mailto:${client.email}`}
                      className="text-xs text-info-600 dark:text-info-400 hover:underline truncate min-w-0"
                    >
                      {client.email}
                    </a>
                  </div>
                )}
              </div>

              {/* Read-only role badge - full management lives in the side panel */}
              <div className="flex-shrink-0">
                <Badge color="blue">{client.role_name}</Badge>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('No clients assigned to this matter')}
          </p>
          {canEdit && (
            <Button variant="outline" size="small" onClick={openAddParty} disabled={opening}>
              {t('Add a party')}
            </Button>
          )}
        </div>
      )}
    </GlassCard>
  );
};

export default MatterClients;
